import { Component, Inject, OnInit } from '@angular/core';
import { UserService } from 'src/app/core/api/user/user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { ChipContent } from 'src/app/shared/models/chip-content/chip-content';
import { MultiSelectChip } from '../filter-container/filter-container.component';
import { DialogService } from 'src/app/core/services/dialog/dialog.service';
import { DialogModalData } from '../../molecules/dialog-modal/dialog-modal.component';
import { FormControl, Validators } from '@angular/forms';
import { AuthenticatedUserService } from 'src/app/core/services/authenticated-user/authenticated-user.service';

@Component({
  selector: 'app-business-unification',
  templateUrl: './business-unification.component.html',
  styleUrls: ['./business-unification.component.scss'],
})
/**
 * Business Unification Component
 */
export class BusinessUnificationComponent implements OnInit {
  loading: boolean = false;

  unificationList: any = [];

  labels = {
    savingError: 'unexpected-error',
    rejectConfirm: 'business-unification-reject-confirm',
    rejectConfirmMessage: 'business-unification-reject-confirm-message',
    rejectWithSuccess: 'business-unification-reject-success',
    rejectError: 'business-unification-reject-error',
    acceptConfirm: 'business-unification-accept-confirm',
    acceptConfirmMessage: 'business-unification-accept-confirm-message',
    acceptWithSuccess: 'business-unification-accept-success',
    acceptError: 'business-unification-accept-error',
    confirm: 'confirm',
    cancel: 'cancel',
  };

  statusChips: MultiSelectChip[] = this.fillStatusChips();

  filters: any = {
    status: [],
    search: '',
  };

  search = new FormControl('', [Validators.minLength(3)]);

  /**
   * @constructor
   */
  constructor(
    private readonly translationService: TranslationService,
    private readonly userService: UserService,
    private readonly snackBarService: SnackBarService,
    private readonly dialogService: DialogService,
    private authenticatedUserService: AuthenticatedUserService,
    @Inject('Window') private window: Window
  ) {}

  /**
   * On Init
   */
  ngOnInit() {
    this.translationService.translateObject(this.labels);
    this.getUnificationList();
  }

  /**
   * Get a filtered list of business unification requests
   */
  getUnificationList() {
    this.filters.search = this.search.getRawValue()!;
    this.userService
      .getBusinessUnificationRequests(this.filters)
      .subscribe((unificationRequests) => {
        this.unificationList = unificationRequests;
      });
  }

  /**
   * Updates Status Filter
   * @param {any} event
   */
  updateStatusFilter(event: any) {
    this.getUnificationList();
  }

  /**
   * Show business unification details by id
   * @param {number} id
   */
  showUnificationDetails(id: number) {
    this.dialogService.openShowBusinessUnificationDetailsModal(
      id,
      (response) => {
        if (response.reload) {
          this.getUnificationList();
        }
      }
    );
  }

  /**
   * Show business unification creation modal
   */
  showNewBusinessUnificationModal() {
    this.dialogService.openNewBusinessUnificationModal(() => {
      this.getUnificationList();
    });
  }

  /**
   * Accept business unification by id
   * @param {number} id
   */
  acceptUnification(id: number) {
    const dialogData: DialogModalData = {
      title: this.labels.acceptConfirm,
      secondaryContent: [
        {
          content: this.labels.acceptConfirmMessage,
          style: {
            color: 'var(--Cinzas-400, #19212B)',
            fontFamily: 'Helvetica',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '125%',
          },
        },
      ],
      successButtonText: this.labels.confirm,
      failButtonText: this.labels.cancel,
    };
    this.dialogService.openDialog(
      dialogData,
      () => {
        this.userService.acceptBusinessUnificationRequest(id).subscribe({
          next: () => {
            this.snackBarService.success(this.labels.acceptWithSuccess);
            this.getUnificationList();
          },
          error: () => {
            this.snackBarService.error(this.labels.acceptError);
          },
        });
      },
      () => {}
    );
  }

  /**
   * Reject a business unification request by id
   * @param {number} id
   */
  rejectUnification(id: number) {
    const dialogData: DialogModalData = {
      title: this.labels.rejectConfirm,
      secondaryContent: [
        {
          content: this.labels.rejectConfirmMessage,
          style: {
            color: 'var(--Cinzas-400, #19212B)',
            fontFamily: 'Helvetica',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '125%',
          },
        },
      ],
      successButtonText: this.labels.confirm,
      failButtonText: this.labels.cancel,
    };
    this.dialogService.openDialog(
      dialogData,
      () => {
        this.userService.rejectBusinessUnificationRequest(id).subscribe({
          next: () => {
            this.snackBarService.success(this.labels.rejectWithSuccess);
            this.getUnificationList();
          },
          error: () => {
            this.snackBarService.error(this.labels.rejectError);
          },
        });
      },
      () => {}
    );
  }

  /**
   * Returns current language
   * @return {string}
   */
  getLanguage() {
    return this.translationService.getActiveLang();
  }

  /**
   * Returns true if the user is global admin
   * @return {boolean}
   */
  isGlobalAdmin(): boolean {
    return this.authenticatedUserService.isGlobalAdmin();
  }

  /**
   * Factory method for status multiselect instances
   * @return {MultiSelectChip[]}
   */
  fillStatusChips(): MultiSelectChip[] {
    const statusChips: MultiSelectChip[] = [
      {
        value: 'Pending',
        isActive: false,
        filterType: 'Pending',
        id: 'PENDING',
      },
      {
        value: 'Rejected',
        isActive: false,
        filterType: 'Rejected',
        id: 'REJECTED',
      },
      {
        value: 'In Progress',
        isActive: false,
        filterType: 'In Progress',
        id: 'IN_PROGRESS',
      },
      {
        value: 'Unified',
        isActive: false,
        filterType: 'Unified',
        id: 'UNIFIED',
      },
    ];
    return statusChips;
  }

  /**
   *
   * @return {Array<MultiSelectChip>}
   */
  getSelectedStatusChips(): Array<MultiSelectChip> {
    return this.statusChips.filter((chip) => chip.isActive);
  }

  /**
   * Selects or unselects chip and update filter object
   * @param {ChipContent | MultiSelectChip} chip
   * @param { string } filterName
   * @param {any} event
   */
  onChipSelectionChange(
    chip: ChipContent | MultiSelectChip,
    filterName: string,
    event?: any
  ) {}

  /**
   * Download the unification report zip file
   * @param {number} id
   */
  downloadReportReportFile(id: number) {
    this.userService.getBusinessUnificationReport(id).subscribe((response) => {
      this.window.location.assign(response);
    });
  }
}
